import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import httpClient, { extractErrorMessage } from '../Services/httpClient';

const ENTITY_NAME = 'LemonCoinPriceConfiguration';

const parsePriceBRL = (value) => {
    if (value === null || value === undefined) return null;
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isNaN(parsed) ? null : parsed;
};

const formatDateShort = (timestamp) =>
    new Date(timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const formatDateFull = (timestamp) => new Date(timestamp).toLocaleString('pt-BR');

const LemonCoinPriceHistoryPanel = () => {
    const [history, setHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await httpClient.get('/backoffice/activity-log', {
                    params: { page: 1, pageSize: 100, entityName: ENTITY_NAME }
                });

                const ascending = [...response.data.items].reverse();
                let previousPrice = null;
                const parsed = [];
                ascending.forEach((item) => {
                    const priceField = item.changedFields.find((f) => f.field === 'PriceBRL');
                    const price = parsePriceBRL(priceField?.newValue);
                    if (price === null) return;
                    parsed.push({
                        timestamp: item.timestamp,
                        price,
                        variation: previousPrice === null ? null : price - previousPrice
                    });
                    previousPrice = price;
                });

                setHistory(parsed);
            } catch (err) {
                setError(extractErrorMessage(err));
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    return (
        <Paper elevation={0} sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ color: '#f77513', fontWeight: 'bold', mb: 2 }}>
                Histórico de Alterações
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {isLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={28} style={{ color: '#f77513' }} />
                </Box>
            ) : history.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhuma alteração de preço registrada.</Typography>
            ) : (
                <>
                    <Box sx={{ width: '100%', height: 260, mb: 3 }}>
                        <ResponsiveContainer>
                            <LineChart data={history} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="timestamp" tickFormatter={formatDateShort} minTickGap={30} />
                                <YAxis domain={['auto', 'auto']} tickFormatter={(v) => `R$ ${v}`} width={80} />
                                <Tooltip
                                    labelFormatter={formatDateFull}
                                    formatter={(value) => [`R$ ${Number(value).toFixed(4)}`, 'Preço']}
                                />
                                <Line type="monotone" dataKey="price" stroke="#f77513" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>

                    <TableContainer variant="outlined" component={Paper} sx={{ borderRadius: '14px' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow style={{ backgroundColor: '#fff4ec' }}>
                                    <TableCell>Data</TableCell>
                                    <TableCell>Valor (R$)</TableCell>
                                    <TableCell>Variação</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[...history].reverse().map((item) => (
                                    <TableRow key={item.timestamp}>
                                        <TableCell>{formatDateFull(item.timestamp)}</TableCell>
                                        <TableCell>R$ {item.price.toFixed(4)}</TableCell>
                                        <TableCell>
                                            {item.variation === null ? (
                                                <Typography variant="caption" color="text.secondary">—</Typography>
                                            ) : (
                                                <Chip
                                                    label={`${item.variation >= 0 ? '+' : ''}R$ ${item.variation.toFixed(4)}`}
                                                    size="small"
                                                    style={
                                                        item.variation > 0
                                                            ? { backgroundColor: '#e8f5e9', color: '#2e7d32' }
                                                            : item.variation < 0
                                                                ? { backgroundColor: '#fdecea', color: '#c62828' }
                                                                : {}
                                                    }
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Paper>
    );
};

export default LemonCoinPriceHistoryPanel;
